import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Typography, 
    Button, 
    Box,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip,
    Fade,
    makeStyles
} from '@material-ui/core';
import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { getUserToken } from '../../services/api';
import { auth } from '../../services/firebase/firebase';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        minHeight: 400
    },
    title: {
        marginBottom: theme.spacing(2),
        textAlign: 'center'
    },
    description: {
        marginBottom: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary
    },
    iframeContainer: {
        border: '2px solid #e0e0e0',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        height: 400,
        position: 'relative',
        transition: 'all 0.3s ease-in-out'
    },
    iframeContainerExpanded: {
        height: 'calc(100vh - 120px)',
        borderRadius: theme.spacing(0.5)
    },
    iframe: {
        width: '100%',
        height: '100%',
        border: 'none'
    },
    expandControls: {
        position: 'absolute',
        top: '1px',
        right: '1px',
        zIndex: 1000,
        display: 'flex',
        gap: theme.spacing(0.5)
    },
    expandButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(4px)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            transform: 'scale(1.05)'
        },
        transition: 'all 0.2s ease-in-out'
    },
    tipButton: {
        backgroundColor: 'rgba(255, 235, 59, 0.9)',
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: 'rgba(255, 235, 59, 0.95)'
        }
    },

    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 1
    },
    testInfo: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.info.light,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2)
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2)
    },
    centeredButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(3)
    },
    backButton: {
        minWidth: 120,
        padding: theme.spacing(1.5, 3),
        backgroundColor: '#ffffff',
        color: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main
        }
    },
    nextButton: {
        minWidth: 220,
        padding: theme.spacing(1.5, 3),
        fontSize: '16px',
        fontWeight: 'bold',
        borderRadius: 8
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1.5),
        maxWidth: 300,
        '& div': {
            marginBottom: theme.spacing(0.5),
            '&:last-child': {
                marginBottom: 0
            }
        }
    },
    tooltipArrow: {
        display: 'none'
    },
    titleCongratulations: {
        color: theme.palette.success.main,
        fontSize: '2em',
        fontWeight: 'bold'
    }
}));

export default function ChatbotTestStep({ checkTokenExpiration }) {
    const classes = useStyles();
    const [{ wizardData, expandedMode }, dispatch] = useStateValue();
    const [loading, setLoading] = useState(true);
    const [chatbotUrl, setChatbotUrl] = useState('');
    const [iframeError, setIframeError] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(null);
    const [userToken, setUserToken] = useState('');

    const iframeRef = useRef(null);

    // Create anonymous user and get token
    const fetchUserToken = async () => {
        try {
            // Sign in anonymously to Firebase
            const userCredential = await auth.signInAnonymously();
            const uid = userCredential.user.uid;
            console.log('🔑 Anonymous user UID:', uid);

            // Get token from /login endpoint using the Firebase UID
            console.log('📞 Calling getUserToken with UID:', uid);
            const response = await getUserToken({ uid });
            console.log('📥 Login API response:', response);
            console.log('🎫 Token received:', response.data);
            
            setUserToken(response.data);
            console.log('✅ User token set successfully');
        } catch (error) {
            console.error('❌ Error fetching user token:', error);
            console.error('❌ Error details:', error.response?.data);
            console.error('❌ Error status:', error.response?.status);
        }
    };

    useEffect(() => {
        // Fetch user token on component mount
        fetchUserToken();
    }, []);

    useEffect(() => {
        // Generate chatbot URL based on the created chatbot
        const generateChatbotUrl = () => {
            let chatbotAppUrl;
            
            // Use the deployed SAIL chatbot app with session persistence
            // chatbotAppUrl = "https://sail-staging-open-session.web.app/?";
            chatbotAppUrl = process.env.REACT_APP_CHATBOT_URL_SESSION;
            
            // For demo purposes, use a simple URL without dynamic parameters
            let fullUrl;
            const isDemo = !wizardData.chatbotId || wizardData.chatbotId.startsWith('demo-chatbot');
            
            if (!isDemo) {
                // Use token-based URL if we have a token, otherwise simple format
                if (userToken) {
                    fullUrl = `${chatbotAppUrl}&token=${userToken}&id=${wizardData.chatbotId}`;
                    console.log('🎫 Using token-based URL with token:', userToken);
                } else {
                    fullUrl = `${chatbotAppUrl}&id=${wizardData.chatbotId}`;
                    console.log('⚠️ No token available, using simple URL');
                }
            } else {
                // For demo/testing, use the simple URL without parameters
                fullUrl = chatbotAppUrl;
                console.log('🧪 Using demo URL');
            }
            
            console.log('🌐 Generated chatbot URL:', fullUrl);
            console.log('🎫 Current userToken state:', userToken);
            
            setChatbotUrl(fullUrl);
            setLoading(false);
            
            // Set a timeout to detect if iframe takes too long to load
            const timeout = setTimeout(() => {
                console.warn('Iframe loading timeout for:', fullUrl);
                setIframeError(true);
            }, 20000); // 20 seconds timeout
            
            setLoadingTimeout(timeout);
        };

        // Simulate loading time for chatbot initialization
        setTimeout(generateChatbotUrl, 2000);
    }, [wizardData.chatbotId, userToken]);

    const postBypassDraftMessage = useCallback(() => {
        try {
            if (!iframeRef.current) return;
            const targetOrigin = new URL(chatbotUrl).origin;
            iframeRef.current.contentWindow.postMessage("chatbotStatus", targetOrigin);
        } catch (err) {
            console.error('Error posting chatbotStatus to iframe:', err);
        }
    }, [chatbotUrl]);

    useEffect(() => {
        const handleIframeMessage = (e) => {
            try {
                const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                if (data && data.appMounted === "true") {
                    postBypassDraftMessage();
                }
            } catch (err) {
                // Ignore non-JSON messages
            }
        };
        window.addEventListener('message', handleIframeMessage);
        return () => window.removeEventListener('message', handleIframeMessage);
    }, [postBypassDraftMessage]);
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (loadingTimeout) {
                clearTimeout(loadingTimeout);
            }
        };
    }, [loadingTimeout]);



    const handleMakeOnline = () => {
        // Check token expiration before proceeding (if token exists)
        if (checkTokenExpiration && !checkTokenExpiration()) {
            // Token expired, checkTokenExpiration handles the error display
            return;
        }

        // Store chatbot URL for next step
        dispatch({
            type: 'UPDATE_WIZARD_DATA',
            data: { 
                chatbotUrl
            }
        });

        // Navigate to next step immediately (no API call delay)
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.USER_DETAILS
        });
    };

    const handleToggleExpand = () => {
        dispatch({ type: 'TOGGLE_EXPANDED_MODE' });
    };

    const handleNeedHelp = () => {
        // Store current step for return navigation
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: WIZARD_STEPS.CHATBOT_TEST
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
    };

    return (
        <Box className={classes.stepContainer}>
            {!expandedMode && (
                <Typography variant="h6" className={classes.title}>
                    <span className={classes.titleCongratulations}>Congratulations</span> – Your Agent is in Draft Mode
                </Typography>
            )}
            
            {!expandedMode && (
                <Typography variant="body1" className={classes.description}>
                    Test it below to make sure it's working as expected.
                </Typography>
            )}

            <Paper className={`${classes.iframeContainer} ${expandedMode ? classes.iframeContainerExpanded : ''}`}>
                {/* Expand Controls */}
                <Fade in={!loading}>
                    <Box className={classes.expandControls}>
                        <Tooltip 
                            key={`focus-${expandedMode}`} 
                            title={expandedMode ? "Exit Focus Mode" : "Expand Focus Mode"}
                            classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                            placement="top"
                        >
                            <IconButton 
                                size="small" 
                                className={classes.expandButton}
                                onClick={handleToggleExpand}
                            >
                                {expandedMode ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Fade>

                {loading && (
                    <Box className={classes.loadingOverlay}>
                        <CircularProgress />
                        <Typography variant="body2" style={{ marginLeft: 16 }}>
                            Initializing your chatbot...
                        </Typography>
                    </Box>
                )}
                
                {!loading && !iframeError && (
                    <iframe
                        ref={iframeRef}
                        src={chatbotUrl}
                        className={classes.iframe}
                        title={`${wizardData.chatbotName} - Test Chat`}
                        allow="microphone; camera"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        onError={() => {
                            console.error('Iframe failed to load:', chatbotUrl);
                            setIframeError(true);
                        }}
                        onLoad={(e) => {
                            console.log('Iframe loaded successfully:', chatbotUrl);
                            // Clear the timeout since iframe loaded successfully
                            if (loadingTimeout) {
                                clearTimeout(loadingTimeout);
                                setLoadingTimeout(null);
                            }
                            // For cross-origin iframes, we can't access contentDocument
                            // If we get here, the iframe loaded successfully
                            setIframeError(false);
                            // Inform the iframe to bypass draft status if allowed by origin
                            postBypassDraftMessage();
                            // Retry shortly in case the listener isn't ready yet inside the iframe
                            setTimeout(() => postBypassDraftMessage(), 500);
                        }}
                    />
                )}
                
                {!loading && iframeError && (
                    <Box className={classes.loadingOverlay}>
                        <Box textAlign="center">
                            <Typography variant="h6" gutterBottom>
                                Chatbot Preview Not Available
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                The chatbot app is not currently running on {chatbotUrl}
                            </Typography>
                            
                            <Box style={{ 
                                backgroundColor: '#f5f5f5', 
                                padding: 16, 
                                borderRadius: 8, 
                                marginTop: 16, 
                                marginBottom: 16,
                                textAlign: 'center'
                            }}>
                                <Typography variant="body2">
                                    The deployed chatbot app at<br/>
                                    <strong>{process.env.REACT_APP_CHATBOT_URL_SESSION}</strong><br/>
                                    may be temporarily unavailable.
                                </Typography>
                            </Box>
                            
                            <Typography variant="body2" style={{ marginBottom: 16 }}>
                                <strong>Your agent "{wizardData.chatbotName}" has been created successfully!</strong><br/>
                                You can proceed to publish it or start the chatbot app to test it.
                            </Typography>
                            
                            <Box>
                                <Button
                                    variant="outlined"
                                    onClick={() => setIframeError(false)}
                                    style={{ marginRight: 8 }}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        // Check token expiration before proceeding (if token exists)
                                        if (checkTokenExpiration && !checkTokenExpiration()) {
                                            // Token expired, checkTokenExpiration handles the error display
                                            return;
                                        }

                                        dispatch({
                                            type: 'SET_CURRENT_STEP',
                                            step: WIZARD_STEPS.USER_DETAILS
                                        });
                                    }}
                                >
                                    Skip to Publish
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>

            {!expandedMode && (
                <>
                    <Box className={classes.centeredButtonContainer}>
                        <Tooltip 
                            title={
                                <Box>
                                    <div>• Make the agent available to other users</div>
                                    <div>• Refine the agent to improve user experience</div>
                                    <div>• Full access to Agent Admin Panel</div>
                                </Box>
                            }
                            classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                            placement="top"
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleMakeOnline}
                                className={classes.nextButton}
                                disabled={loading}
                            >
                                Go Live With SAIL
                            </Button>
                        </Tooltip>
                    </Box>

                    <Box className={classes.buttonContainer}>
                        <Tooltip 
                            title="Click here if you need support"
                            classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                            placement="top"
                        >
                            <Button
                                variant="outlined"
                                onClick={handleNeedHelp}
                                className={classes.backButton}
                            >
                                Need help?
                            </Button>
                        </Tooltip>
                        <Box /> {/* Placeholder for future button on the right */}
                    </Box>
                </>
            )}
            

        </Box>
    );
}
